import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import {
    useForm,
    FormProvider,
    UseFormReturn,
    UseFormProps,
    FieldValues,
    Path
} from 'react-hook-form';
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

// Create a context to hold the form type and required fields information
const ZodFormContext = createContext<{
    formType: unknown;
    requiredFields: Set<string>;
}>({
    formType: {},
    requiredFields: new Set()
});

// Props for the TypedFormProvider
interface ZodFormProviderProps<T extends FieldValues> {
    children: ReactNode;
    methods: UseFormReturn<T>;
    requiredFields?: Set<string>;
}

// The TypedFormProvider component
export function ZodFormProvider<T extends FieldValues>({
    children,
    methods,
    requiredFields = new Set()
}: ZodFormProviderProps<T>) {
    return (
        <ZodFormContext.Provider value={{ formType: {} as T, requiredFields }}>
            <FormProvider {...methods}>
                {children}
            </FormProvider>
        </ZodFormContext.Provider>
    );
}

// Helper function to extract required fields from a Zod schema
export function extractRequiredFields(schema: z.ZodTypeAny): Set<string> {
    const requiredFields = new Set<string>();

    try {
        // Check if it's an object schema
        if (schema instanceof z.ZodObject) {
            const shape = schema._def.shape();

            // Examine each property in the schema
            Object.entries(shape).forEach(([key, value]) => {
                let fieldSchema = value as z.ZodTypeAny;

                // Unwrap any optional wrapper
                if (fieldSchema instanceof z.ZodOptional) {
                    // If it's optional, it's not required
                    return;
                }

                // Handle nullable fields (nullable doesn't mean optional)
                if (fieldSchema instanceof z.ZodNullable) {
                    fieldSchema = fieldSchema._def.innerType;
                    // If the inner type is optional, it's not required
                    if (fieldSchema instanceof z.ZodOptional) {
                        return;
                    }
                }

                // If we got here, the field is required
                requiredFields.add(key);
            });
        }
    } catch (error) {
        console.warn('Error extracting required fields from schema:', error);
    }

    return requiredFields;
}

// Extract Zod schema from resolver if present
function extractZodSchema(options?: UseFormProps<any>): z.ZodTypeAny | null {
    if (!options?.resolver) return null;

    // Try to extract schema from zodResolver
    try {
        const resolverSource = options.resolver.toString();
        // This is a heuristic - check if it's a zodResolver
        if (resolverSource.includes('zodResolver') && options.resolver['_def']) {
            return options.resolver['_def'].schema;
        }
    } catch (e) {
        // Silently fail if we can't extract the schema
    }

    return null;
}

// Hook to create a form with type checking - supports the original API
export function useZodForm<T extends FieldValues>(options?: UseFormProps<T>) {
    // Extract schema if possible
    let requiredFields = new Set<string>();
    const schema = useMemo(() => {
        if (options?.resolver) {
            // Try to access the schema from the resolver
            try {
                // Check if resolver is from zodResolver
                if (typeof options.resolver === 'function') {
                    const resolverStr = options.resolver.toString();
                    // Look for zodResolver signature
                    if (resolverStr.includes('zodResolver')) {
                        // Extract schema from resolver options if available
                        if ((options.resolver as any)._def && (options.resolver as any)._def.schema) {
                            const zodSchema = (options.resolver as any)._def.schema;
                            requiredFields = extractRequiredFields(zodSchema);
                            return zodSchema;
                        }
                    }
                }
            } catch (e) {
                console.warn('Could not extract schema from resolver:', e);
            }
        }
        return null;
    }, [options?.resolver]);

    // Create form with original options
    const methods = useForm<T>(options);

    return {
        ...methods,
        requiredFields
    };
}

// Hook to access required field information
export function useRequiredFields() {
    const { requiredFields } = useContext(ZodFormContext);
    return requiredFields;
}

// Helper to get the form type from the context
export function useFormType<T extends FieldValues = FieldValues>() {
    return useContext(ZodFormContext).formType as T;
}

// This type will be used to constrain the name prop in our Input component
export type TypedInputProps<T extends FieldValues> = {
    name: Path<T>;
};